import React, { useState } from 'react'
import { Line, Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js'
import 'chartjs-adapter-luxon'
import { usePerformanceDashboard, usePerformanceHistory } from '../../utils/usePerformanceData'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
)

interface PerformanceMetrics {
  timestamp: string
  endpoint: string
  responseTime: number
  cacheHit: boolean
  jiraApiCalls: number
  errorRate: number
  userId?: string
}

interface DashboardStats {
  totalRequests: number
  averageResponseTime: number
  cacheHitRate: number
  errorRate: number
  jiraApiCallsReduction: number
  activeUsers: number
}

interface EndpointMetrics {
  endpoint: string
  totalRequests: number
  averageResponseTime: number
  p50ResponseTime: number
  p95ResponseTime: number
  p99ResponseTime: number
  cacheHitRate: number
  errorRate: number
  jiraApiCallsPerRequest: number
  lastUpdated: string
}

export const PerformanceDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h')

  // Use React Query hooks for data fetching
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = usePerformanceDashboard(timeRange, 30000)
  const { data: historyData, isLoading: historyLoading } = usePerformanceHistory(timeRange, 'hour', 'responseTime')


  const loading = dashboardLoading || historyLoading
  const error = dashboardError?.message || null

  // Extract data from React Query responses
  const stats = dashboardData?.stats
  const metrics = dashboardData?.metrics || []
  const endpointMetrics = (Array.isArray(dashboardData?.endpointMetrics)
    ? dashboardData.endpointMetrics
    : Object.values(dashboardData?.endpointMetrics || {})) as EndpointMetrics[]

  const responseTimeChartData = {
    labels: historyData?.data?.map(d => new Date(d.timestamp)) || [],
    datasets: [
      {
        label: 'Response Time (ms)',
        data: historyData?.data?.map(d => d.value) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1
      }
    ]
  }

  const cacheHitRateData = {
    labels: ['Cache Hits', 'Cache Misses'],
    datasets: [
      {
        data: stats ? [
          stats.cacheHitRate * 100,
          (1 - stats.cacheHitRate) * 100
        ] : [0, 0],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 1
      }
    ]
  }

  const endpointPerformanceData = {
    labels: endpointMetrics.map(e => e.endpoint.replace('/api/', '')),
    datasets: [
      {
        label: 'Average Response Time (ms)',
        data: endpointMetrics.map(e => e.averageResponseTime),
        backgroundColor: 'rgba(168, 85, 247, 0.8)',
        borderColor: 'rgba(168, 85, 247, 1)',
        borderWidth: 1
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          displayFormats: {
            hour: 'HH:mm',
            day: 'MMM dd'
          }
        }
      },
      y: {
        beginAtZero: true
      }
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading performance data
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <main className="min-h-screen bg-white" role="main" aria-labelledby="performance-main-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <header className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 id="performance-main-heading" className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                Performance Dashboard
              </h1>
              <p className="text-sm sm:text-base text-gray-600 max-w-3xl">
                Monitor API performance, cache efficiency, and system health metrics.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as any)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="1h">Last Hour</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
              <button
                onClick={() => window.location.reload()}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </header>

        {/* Key Metrics Cards */}
        {stats && (
          <section aria-labelledby="metrics-section" className="mb-6 sm:mb-8">
            <h2 id="metrics-section" className="sr-only">Performance Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">RT</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Avg Response Time
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.averageResponseTime.toFixed(0)}ms
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">CH</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Cache Hit Rate
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {(stats.cacheHitRate * 100).toFixed(1)}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">RQ</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Requests
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.totalRequests.toLocaleString()}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">ER</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Error Rate
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {(stats.errorRate * 100).toFixed(2)}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Charts */}
        <section aria-labelledby="charts-section" className="mb-6 sm:mb-8">
          <h2 id="charts-section" className="sr-only">Performance Charts</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Response Time Trend */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Response Time Trend</h3>
              <div className="h-64 w-full">
                <Line
                  data={responseTimeChartData}
                  options={{
                    ...chartOptions,
                    maintainAspectRatio: false,
                    responsive: true
                  }}
                />
              </div>
            </div>

            {/* Cache Hit Rate */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Cache Performance</h3>
              <div className="h-64 flex items-center justify-center">
                <div className="w-40 h-40 sm:w-48 sm:h-48">
                  <Doughnut
                    data={cacheHitRateData}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Endpoint Performance */}
        <section aria-labelledby="endpoint-performance-section" className="mb-6 sm:mb-8">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <h3 id="endpoint-performance-section" className="text-lg font-medium text-gray-900 mb-4">Endpoint Performance</h3>
            <div className="h-64 w-full">
              <Bar
                data={endpointPerformanceData}
                options={{
                  ...chartOptions,
                  maintainAspectRatio: false,
                  responsive: true
                }}
              />
            </div>
          </div>
        </section>

        {/* Endpoint Details Table */}
        <section aria-labelledby="endpoint-details-section">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 id="endpoint-details-section" className="text-lg leading-6 font-medium text-gray-900">
                Endpoint Details
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Detailed performance metrics for each API endpoint
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Endpoint
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requests
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Response Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      P95 Response Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cache Hit Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Error Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {endpointMetrics.filter(endpoint => endpoint && endpoint.endpoint).map((endpoint, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {endpoint.endpoint || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(endpoint.totalRequests || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(endpoint.averageResponseTime || 0).toFixed(0)}ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(endpoint.p95ResponseTime || 0).toFixed(0)}ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(endpoint.cacheHitRate || 0) > 0.8
                          ? 'bg-green-100 text-green-800'
                          : (endpoint.cacheHitRate || 0) > 0.6
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {((endpoint.cacheHitRate || 0) * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(endpoint.errorRate || 0) < 0.01
                          ? 'bg-green-100 text-green-800'
                          : (endpoint.errorRate || 0) < 0.05
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {((endpoint.errorRate || 0) * 100).toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}

export default PerformanceDashboard